// =============================================================================
// resourceImages.ts — pure helpers for Resources content-page images (db-110)
// =============================================================================
// Side-effect-free helpers shared by the Resources editor upload path and the
// render-time image signer. None of this touches Supabase — it is the upload
// guard (size + MIME whitelist) and the one object-key extraction that must stay
// identical wherever a resource-image URL is produced or parsed.
//
// Context (B14 Job 2): the `resource-images` bucket was PUBLIC; db-185 makes it
// PRIVATE (5 MB limit + a MIME whitelist), so images are now read via a signed
// URL. The stored page HTML may hold a bare key, a LEGACY full public URL, or a
// signed URL — resourceImageKey() reduces any of them to the object key so the
// render-time signer can re-sign fresh every time.
// =============================================================================

// Maximum upload size for a resource image, in bytes. 5 MB = 5242880.
//
// ⚠️ MUST match the `resource-images` bucket's `file_size_limit` (db-185). The
// bucket is the real enforcement; this client check just fails fast with a
// readable sentence instead of a raw storage error after a pointless upload.
export const RESOURCE_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
export const RESOURCE_IMAGE_MAX_LABEL = `${RESOURCE_IMAGE_MAX_BYTES / (1024 * 1024)} MB`;

// The MIME types a resource image may be. ⚠️ MUST match the bucket's
// `allowed_mime_types` (db-185). SVG is DELIBERATELY excluded (John, 29 Aug): an
// SVG can carry script, and the picker offers image/* broadly.
export const RESOURCE_IMAGE_MIME_WHITELIST = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
] as const;

// Extension → MIME fallback for the guard, for the rare File with an empty `type`.
const EXT_MIME: Record<string, string> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  svg: "image/svg+xml", // listed only so it is recognised and then REJECTED
};

function mimeOf(file: { type?: string; name?: string }): string {
  const t = String(file.type || "").toLowerCase();
  if (t) return t;
  const ext =
    String(file.name || "")
      .split(".")
      .pop()
      ?.toLowerCase() || "";
  return EXT_MIME[ext] || "";
}

// "" when the file is an acceptable resource image, otherwise the message to show.
// Takes anything with size/type/name (a File) so it stays testable without a DOM.
export function resourceImageError(
  file: { size?: number; type?: string; name?: string } | null
): string {
  if (!file) return "";
  const mime = mimeOf(file);
  if (!(RESOURCE_IMAGE_MIME_WHITELIST as readonly string[]).includes(mime)) {
    return "That file type isn't allowed. Use a PNG, JPEG, GIF or WebP image (SVG is not permitted).";
  }
  const n = Number(file.size);
  if (Number.isFinite(n) && n > RESOURCE_IMAGE_MAX_BYTES) {
    return `That image is too large. The limit is ${RESOURCE_IMAGE_MAX_LABEL} — please use a smaller image.`;
  }
  return "";
}

// The bucket path segment that marks a resource-image URL, in every URL shape
// Supabase produces: /object/public/…, /object/sign/…, /object/authenticated/….
const MARKER = "/resource-images/";

// Reduce a stored <img src> to the storage object KEY, or "" if it isn't a
// resource-image reference. Handles a bare key, a legacy full public URL, and a
// signed URL (drops the ?token). Mirrors getFileSignedUrl's legacy-URL handling
// for zephyr-files (db-168).
export function resourceImageKey(src: string | null | undefined): string {
  const s = String(src || "").trim();
  if (!s) return "";
  const idx = s.indexOf(MARKER);
  if (idx !== -1) {
    const rest = s
      .slice(idx + MARKER.length)
      .split("?")[0]
      .split("#")[0];
    try {
      return decodeURIComponent(rest);
    } catch {
      return rest; // leave the raw path if it isn't valid percent-encoding
    }
  }
  // A bare object key (no scheme, no path, no marker) — accept only a plain
  // "<name>.<ext>" so we never mistake an external image URL for one of ours.
  if (!s.includes("://") && !s.includes("/") && /\.[a-z0-9]+$/i.test(s)) return s;
  return "";
}
